// Minimal TOTP (RFC 6238) and base32 for browser, no deps

// Base32 decode (RFC 4648, no padding)
export function base32Decode(str: string): Uint8Array {
	// Handle empty or undefined inputs
	if (!str || str.trim() === "") {
		return new Uint8Array(0);
	}

	const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
	let bits = 0;
	let value = 0;
	let index = 0;
	const cleanedInput = str.replace(/[^A-Za-z2-7]/g, "").toUpperCase();

	if (cleanedInput.length === 0) {
		return new Uint8Array(0);
	}

	const output = new Uint8Array(Math.ceil((cleanedInput.length * 5) / 8));

	for (let i = 0; i < cleanedInput.length; i++) {
		const idx = alphabet.indexOf(cleanedInput[i]);
		if (idx === -1) continue;
		value = (value << 5) | idx;
		bits += 5;
		if (bits >= 8) {
			output[index++] = (value >>> (bits - 8)) & 0xff;
			bits -= 8;
		}
	}

	// Return empty array if no valid characters were processed
	if (index === 0) {
		return new Uint8Array(0);
	}

	return output.slice(0, index);
}

// TOTP generator (SHA-1, 6 digits, 30s step)
export async function generateTOTP(
	secret: string,
	time?: number,
): Promise<string> {
	try {
		// Clean secret and validate
		const cleanSecret = secret.replace(/\s+/g, "").toUpperCase();
		if (!cleanSecret) {
			return "------";
		}

		// Decode Base32 secret to bytes
		const key = base32Decode(cleanSecret);
		if (key.length === 0) {
			return "------";
		}

		const epoch = Math.floor((time ?? Date.now()) / 1000);
		const step = Math.floor(epoch / 30);

		// 8-byte buffer, big-endian
		const msg = new Uint8Array(8);
		let s = step;
		for (let i = 7; i >= 0; i--) {
			msg[i] = s & 0xff;
			s >>>= 8;
		}

		// HMAC-SHA1
		const cryptoKey = await window.crypto.subtle.importKey(
			"raw",
			key,
			{ name: "HMAC", hash: "SHA-1" },
			false,
			["sign"],
		);
		const hmac = new Uint8Array(
			await window.crypto.subtle.sign("HMAC", cryptoKey, msg),
		);
		const offset = hmac[hmac.length - 1] & 0xf;
		const code =
			((hmac[offset] & 0x7f) << 24) |
			((hmac[offset + 1] & 0xff) << 16) |
			((hmac[offset + 2] & 0xff) << 8) |
			(hmac[offset + 3] & 0xff);

		const otp = (code % 1e6).toString().padStart(6, "0");
		return otp;
	} catch (error) {
		console.error("TOTP generation error:", error);
		return "ERROR";
	}
}
